import React, { Dispatch, FC, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { doRequest } from '@r1-frontend/do-request';
import { ECategory } from '@r1-frontend/api-domru/api-profile/v1/spec-offer/update/dto/ResponseDto';
import { getInfoContacts, IResponseDto } from '@r1-frontend/api-domru/api-profile/v2/info/contacts';
import { ICurrentCity } from '@r1-frontend/entities/City/models/ICurrentCity';

import { dataLayerPush } from '@r1-frontend/analytics/dataLayerPush';

import Throbber from '@r1-frontend/ui-react/components/loaders/Throbber';
import DefaultPopup from '@r1-frontend/ui-react/components/popups/defaultPopup';
// @ts-ignore
import { CreatablePhoneSelect } from '@r1-frontend/ui-react/components/select/creatableSelect';

import type { ICostRa } from '~/src/api/content/shop/getShopItems';
import { buyItem, purchaseAdditionalServices } from '~/src/api/shop';
import AuthForm from '~/src/components/AuthForm';
import PopupRequestByCall from '~/src/components/Popups/RequestByCall';
import {
    ACTIONS, BALANCE_ERROR_CODE,
    CREATED_VALUE,
    FIRST_TRIAL_DESC, INITIAL_RESULT, MOBILE_TYPE_ID,
    SECOND_TRIAL_DESC,
    YANDEX_TRIAL_DURATION,
} from '~/src/components/Promo/popups/PartnerPopup/constants';
import type { ICardProps, IPhone, IResultPopupProps, IYandexTrial } from '~/src/components/Promo/popups/PartnerPopup/types';
import YandexPrice from '~/src/components/Promo/YandexPrice';
import YandexResultPopup from '~/src/components/Promo/YandexResultPopup';
import type { TState } from '~/src/store';
import { setStatusCard } from '~/src/store/actionCreators/promo';
import { openChat } from '~/src/store/actions/chat';
import { getCurrentCity } from '~/src/store/selectors/city';
import { selectDomain } from '~/src/store/selectors/domain';

import * as ST from './styled';

const PartnerPopup: FC<ICardProps> = ({
    item,
    returnUrl,
    isYandex,
    isShowMobilePlank,
    sendVasGA,
    isPopupOpen,
    isConnected,
    setIsPopupOpen,
}) => {
    const {
        id,
        title,
        costRa,
        body_full: bodyFull,
        category: {
            buy_by_call: buyByCall,
            buy_with_check_phone: buyWithCheckPhone,
        } = {},
        partnerVasContents,
    } = item;

    const [isOpen, setIsOpen] = useState(false);
    const [showAuthPopup, setShowAuthPopup] = useState(false);
    const [showRequestPopup, setShowRequestPopup] = useState(false);
    const [isYandexTrial, setIsYandexTrial] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const dispatch = useDispatch();
    const currentCity: ICurrentCity = useSelector(getCurrentCity);
    const auth = useSelector((state: TState) => state.auth);
    const billingDomain = useSelector((state: TState) => selectDomain(state));
    const providerId = useSelector((state: TState) => state.city.provider.providerId);
    const [popupResult, setPopupResult] = useState<IResultPopupProps>({ ...INITIAL_RESULT });
    const [phoneNumber, setPhoneNumber] = useState({ value: '' });
    const [phoneList, setPhoneList] = useState<IPhone[]>([]);
    const [phone, setPhone] = useState<IPhone>({
        value: 0,
        label: '',
    });

    const [isPhoneCreatedValue, setIsPhoneCreatedValue] = useState(false);
    const [yandexTrials, setYandexTrials] = useState<IYandexTrial[]>([]);
    const [selectedYandexTrial, setSelectedYandexTrial] = useState<IYandexTrial | null>(null);

    const getParams = returnUrl
        ? `?referrer=${encodeURIComponent(returnUrl)}`
        : '';

    const onPopup = useCallback(({
        status = 'error',
        title = 'Попробуйте позже',
        message,
        btnText,
        link,
        onBtnClick,
        isSuccess = false,
    }) => {
        setPopupResult((state) => ({
            ...state,
            hasResult: true,
            title,
            message,
            status,
            btnText,
            link,
            onBtnClick,
            isSuccess,
        }));
        setIsOpen(false);
    }, []);

    const onClose = useCallback((_setPopup: Dispatch<SetStateAction<boolean>>) => {
        _setPopup(false);
        setIsPopupOpen(false);
    }, []);

    const sendYandexEvent = (action: string, label: string) => {
        dataLayerPush({
            event: 'UAevent',
            category: 'yandex_plus',
            action,
            label,
        });
    };

    useEffect(() => {
        if (!isPopupOpen) {
            return;
        }
        if (isConnected) {
            title && sendVasGA('click deactivation btn', title);
        }
        if (auth.isAuth || buyByCall) {
            if (buyWithCheckPhone || buyByCall && bodyFull) {
                setIsOpen(true);
            } else {
                handleOrder();
            }
            sendVasGA('click details btn', title);
        } else {
            sendVasGA('click details btn', title);
            setShowAuthPopup(true);
        }
    }, [isPopupOpen]);

    useEffect(() => {
        if (phone && phone?.value === CREATED_VALUE && !isPhoneCreatedValue) {
            setIsPhoneCreatedValue(true);
        } else {
            setIsPhoneCreatedValue(false);
        }
    }, [phone]);

    const checkYandexTrial = () => {
        if (costRa?.length === 1) {
            setIsYandexTrial(true);
        } else if (costRa?.length >= 2) {
            const trials = costRa?.map(parseCostRaToYandexTrial);
            setIsYandexTrial(false);
            setYandexTrials(trials);
            setSelectedYandexTrial(trials[0]);
        }
    };

    const parseCostRaToYandexTrial = (costRa: ICostRa): IYandexTrial => {
        return {
            flagRa: costRa.flag_ra,
            durTypeId: costRa.dur_type_id,
            duration: costRa.duration,
            description: costRa.duration === YANDEX_TRIAL_DURATION ? FIRST_TRIAL_DESC : SECOND_TRIAL_DESC,
            price: costRa.cost,
        };
    };

    const closeResultPopup = useCallback((isSuccess?: boolean) => {
        (popupResult.isSuccess || isSuccess) && dispatchStatusCardWrap(id);
        setPopupResult({ ...INITIAL_RESULT });
        if (isYandex && returnUrl && isConnected) {
            location.href = returnUrl;
        }
        setIsPopupOpen(false);
    }, [id, isConnected, isYandex, popupResult.isSuccess, returnUrl]);

    const onChatClick = () => {
        dispatch(openChat());
        closeResultPopup();
    };

    const dispatchStatusCardWrap = (id: number) => {
        const payload = {
            [id]: {
                isActive: true,
                category: ECategory.connected,
            },
        };
        dispatch(setStatusCard(payload));
    };

    const handleOrder = useCallback(() => {
        if (buyByCall) {
            setIsOpen(false);
            setIsPopupOpen(false);
            setShowRequestPopup(true);
            return;
        }

        title && sendVasGA('click connect btn', title);

        let flagId: number | undefined;
        if (isYandex && selectedYandexTrial) {
            flagId = selectedYandexTrial.flagRa;
        } else if (isYandex && isYandexTrial) {
            const [firstRa] = costRa;
            flagId = firstRa?.flag_ra;
        }

        const phoneParams: {
            phone: string | null,
            contact_id: number | null,
        } = {
            phone: null,
            contact_id: null,
        };

        if (isPhoneCreatedValue) {
            phoneParams.phone = phoneNumber?.value;
        } else {
            phoneParams.contact_id = phone?.value;
        }

        setIsLoading(true);
        const purchaseFunction = buyWithCheckPhone
            ? purchaseAdditionalServices(providerId, auth.accessToken, id, phoneParams, flagId)
            : buyItem(providerId, auth.accessToken, id);

        purchaseFunction
            .then((response) => {
                title && sendVasGA('connect success', title);
                onPopup({
                    status: 'success',
                    title: isYandex ? 'Яндекс Плюс подключен' : 'Приятного пользования!',
                    message: isYandex
                        ? 'Осталось только её активировать. Для этого перейдите в сервисы Яндекса, иначе вы не ' +
                        'сможете начать пользоваться подпиской.'
                        : response.message,
                    btnText: isYandex ? 'Активировать Яндекс Плюс' : '',
                    link: isYandex ? 'https://plus.yandex.ru/domru' : '',
                    isSuccess: true,
                    onBtnClick: () => {
                        !isYandex && closeResultPopup(true);
                    },
                });
            })
            .catch(error => {
                const isBalanceError = error.code === BALANCE_ERROR_CODE;
                title && sendVasGA('connect error', title);
                if (isBalanceError) {
                    isYandex && sendYandexEvent(ACTIONS.activation, 'error replenish balance');
                    onPopup({
                        title: 'На вашем балансе недостаточно средств',
                        message: `Пожалуйста, пополните баланс для подключения ${title}.`,
                        btnText: 'Пополнить баланс',
                        link: '/payments',
                    });
                } else {
                    onPopup({
                        title: `${title} не подключен`,
                        message: <>
                            Пожалуйста, попробуйте еще раз. Если ошибка повторится — напишите нам в&nbsp;
                            <ST.ChatLink onClick={onChatClick}>чат</ST.ChatLink>.
                        </>,
                        btnText: 'Закрыть',
                        onBtnClick: () => closeResultPopup(false),
                    });
                }
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [
        buyByCall,
        buyWithCheckPhone,
        costRa,
        id,
        isPhoneCreatedValue,
        isYandex,
        isYandexTrial,
        onPopup,
        phone?.value,
        phoneNumber?.value,
        selectedYandexTrial,
        title,
    ]);

    useEffect(() => {
        if (auth.accessToken) {
            (async() => {
                // todo- https://ticket.ertelecom.ru/browse/WEB-48708
                // переработать, данные есть в стейте auth
                const resp = await doRequest.apiProfile<IResponseDto>(
                    getInfoContacts({
                        confirmedPhone: 1,
                    }),
                );
                if (resp.isSuccess) {
                    const phoneList = resp.payload.contacts.phone
                        .filter(phone => phone.phoneTypeId === MOBILE_TYPE_ID)
                        .map(item => {
                            return {
                                label: item.contactValue,
                                value: item.id,
                            };
                        });

                    setPhoneList(phoneList);
                    setPhone(phoneList[0]);
                }
            })();
        }
        checkYandexTrial();
    }, [auth.accessToken, billingDomain]);

    return (
        <>
            <ST.ConfirmPopup
                isOpen={isOpen}
                onClose={() => onClose(setIsOpen)}
                city={currentCity}
                label={'InternetOrder'}
                isShowMobilePlank={isShowMobilePlank}
            >
                {isLoading && (
                    <ST.ThrobberWrapper>
                        <Throbber />
                    </ST.ThrobberWrapper>
                )}
                <ST.ConfirmPopupTitle>{title}</ST.ConfirmPopupTitle>

                <ST.ConfirmPopupDescription isYandex={isYandex} dangerouslySetInnerHTML={{ __html: bodyFull }}/>

                {isYandex && <YandexPrice
                    isTrial={isYandexTrial}
                    trials={yandexTrials}
                    price={partnerVasContents?.price}
                    setSelectedTrial={setSelectedYandexTrial}
                    selectedFlagRa={selectedYandexTrial?.flagRa}
                    costRa={costRa}
                />}

                <ST.ConfirmPopupFooter isPhoneCreatedValue={isPhoneCreatedValue}>
                    {
                        !buyByCall &&
                        <CreatablePhoneSelect
                            options={phoneList}
                            onChange={setPhone}
                            value={phone}
                            inputValue={phoneNumber.value}
                            onInputChange={setPhoneNumber}
                            placeholder={'Введите телефон'}
                            inputPlaceHolder={'Номер телефона'}
                        />
                    }

                    <ST.PopupButton
                        disabled={isLoading}
                        onClick={handleOrder}
                        isPhoneCreatedValue={isPhoneCreatedValue}
                        text={isYandex ? isYandexTrial ? 'Попробовать бесплатно' : 'Подключить подписку' : 'Подключить'}
                    />
                </ST.ConfirmPopupFooter>
            </ST.ConfirmPopup>
            <YandexResultPopup
                isOpen={popupResult.hasResult}
                onClose={closeResultPopup}
                title={popupResult.title}
                text={popupResult.message}
                btnText={popupResult.btnText}
                onClick={popupResult.onBtnClick}
                link={popupResult.link}
            />
            <DefaultPopup
                isOpen={showAuthPopup}
                onClose={() => onClose(setShowAuthPopup)}
            >
                <AuthForm getParams={getParams}/>
            </DefaultPopup>

            <PopupRequestByCall
                isOpen={showRequestPopup}
                onClose={() => onClose(setShowRequestPopup)}
                city={currentCity}
                texts={{
                    title: 'Оформление заявки на заказ',
                    subtitle: 'Оставьте заявку, и мы вам перезвоним для уточнения деталей',
                }}
            />
        </>
    );
};

export default PartnerPopup;
